import { auth } from '../services/firebase';
import { logError, LogErrorOptions, LogSeverity } from '../services/logger';
import { categorizeError } from './errorCategory';

const DEBOUNCE_MS = 2000;
const recentErrorAt = new Map<string, number>();

function shouldDebounce(messageKey: string): boolean {
  const now = Date.now();
  const last = recentErrorAt.get(messageKey);
  if (last !== undefined && now - last < DEBOUNCE_MS) {
    return true;
  }
  recentErrorAt.set(messageKey, now);
  if (recentErrorAt.size > 200) {
    const cutoff = now - DEBOUNCE_MS;
    recentErrorAt.forEach((at, key) => {
      if (at < cutoff) recentErrorAt.delete(key);
    });
  }
  return false;
}

function currentAuthUser(): { uid?: string; email?: string | null } | undefined {
  try {
    const user = auth.currentUser;
    if (!user) return undefined;
    return { uid: user.uid, email: user.email };
  } catch {
    return undefined;
  }
}

function debounceKey(error: unknown, contextLabel?: string): string {
  const message =
    error instanceof Error
      ? `${error.name}:${error.message}`
      : typeof error === 'string'
        ? error
        : (() => {
            try {
              return String(error);
            } catch {
              return 'unknown';
            }
          })();
  return contextLabel ? `${contextLabel}::${message}` : message;
}

export interface GlobalLogOptions {
  context?: string;
  metadata?: Record<string, unknown>;
  severity?: LogSeverity;
}

export function logGlobalError(
  error: unknown,
  options: GlobalLogOptions = {},
): void {
  const key = debounceKey(error, options.context);
  if (shouldDebounce(key)) return;

  const category = categorizeError(error);
  const metadata: Record<string, unknown> = {
    ...(options.metadata || {}),
    errorCategory: category.type,
  };

  const payload: LogErrorOptions = {
    context: options.context,
    metadata,
    user: currentAuthUser(),
  };
  if (options.severity) payload.severity = options.severity;
  logError(error, payload);
}

export function logStreamError(
  error: unknown,
  options: GlobalLogOptions = {},
): void {
  logGlobalError(error, options);
}

export const _internal = { shouldDebounce, debounceKey, recentErrorAt };
